/**
 * Segment event payload builders for Native Interactive Site Map (ISM) tracking.
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `segmentTrack(...)`. Property names match
 * Confluence page 5839192072 (footer comment 6000705547) exactly, including its existing
 * `lot_status_typeselected` / `previouslot_status_type` naming — these are not typos to fix.
 *
 * @module nativeInteractiveSiteMap/services/segmentEventBuilder
 */
import getTabTrackingValue from './tabTrackingValue';
import getFloorPlanDetailTabTrackingValue from './floorPlanDetailTabTrackingValue';

/**
 * @param {object} params
 * @param {string} params.interactionType - 'zoom_in' | 'zoom_out' | 'pan' | 'map_type_changed'
 * @param {string} params.inputMethod - 'mouse' | 'touch' | 'button'
 * @param {string} [params.mapType] - 'road' | 'satellite' — only set for map_type_changed
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMapInteractionEvent({ interactionType, inputMethod, mapType }) {
  const customProperties = {
    interaction_type: interactionType,
    input_method: inputMethod,
  };

  if (mapType) {
    customProperties.map_type = mapType;
  }

  return { eventName: 'native_map_interaction', customProperties };
}

/**
 * `lot_status_typeselected` is carried on both paths — on the pill-click path it's the newly
 * selected chip, on the lot-selection path it's the selected lot's own status.
 * `previouslot_status_type` is pill-path only — a lot selection has no "previous chip" to report.
 * `results_count`, `card_count`, `panel_type` and `source` are the core properties and always
 * present.
 *
 * @param {object} params
 * @param {string|null} params.selectedChip - newly selected chip key (pill path) or the selected
 *   lot's status (lot-selection path)
 * @param {string|null} params.previousChip - the chip key that was active before (pill path only)
 * @param {number} params.resultsCount - lot count for the current view
 * @param {number} params.cardCount - number of cards rendered in the active tab
 * @param {'lot_status_type'|'lot_selection'} params.source - what updated the panel
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelUpdatedEvent({ selectedChip, previousChip, resultsCount, cardCount, source, isMobile }) {
  const customProperties = {
    results_count: resultsCount,
    card_count: cardCount,
    panel_type: isMobile ? 'sheet' : 'panel',
    source,
  };

  if (selectedChip != null) {
    customProperties.lot_status_typeselected = selectedChip;
  }

  if (previousChip != null) {
    customProperties.previouslot_status_type = previousChip;
  }

  return { eventName: 'native_map_panel_updated', customProperties };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelScrollEvent() {
  return { eventName: 'native_map_panel_scroll', customProperties: {} };
}

/**
 * Card grid horizontal scroll — fires on both desktop and mobile, unlike buildPanelScrollEvent
 * (the lot-status pill bar), which is mobile-only.
 * @param {object} params
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildCardsScrolledEvent({ isMobile }) {
  return {
    eventName: 'native_map_card_scrolled',
    customProperties: { panel_type: isMobile ? 'sheet' : 'panel' },
  };
}

/**
 * @param {object} params
 * @param {string} params.selectedTab - internal tab constant (constants.js)
 * @param {string} params.previousTab - internal tab constant (constants.js)
 * @param {number} params.resultsCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildTabSelectedEvent({ selectedTab, previousTab, resultsCount }) {
  return {
    eventName: 'native_map_avlbtab_selected',
    customProperties: {
      tab_selected: getTabTrackingValue(selectedTab),
      previous_tab: getTabTrackingValue(previousTab),
      results_count: resultsCount,
    },
  };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyWhenAvailableClickedEvent() {
  return { eventName: 'native_map_notify_clicked', customProperties: {} };
}

/**
 * Mobile drawer interaction — a vertical drag or the drawer being opened. No properties.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDrawerDraggedEvent() {
  return { eventName: 'native_map_sheet_dragged', customProperties: {} };
}

/**
 * @param {object} params
 * @param {object} params.lot - raw store lot object (see services/processLotMapInventory.js)
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.plansCount
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLotSelectedEvent({ lot, planId, specId, plansCount, isMobile }) {
  return {
    eventName: isMobile ? 'native_map_lot_selected_m' : 'native_map_lot_selected_d',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lot.lotId,
      lot_number: lot.displayLabel,
      lot_group: lot.lotGroupName,
      lot_status: lot.status,
      plans_count: plansCount,
      lot_type: lot.lotType,
      lot_size: lot.lotSize,
      lot_facing: lot.lotFacing,
    },
  };
}

/**
 * Fires when the user lands on the Floor Plan Detail view (initial entry, or switching to a
 * different plan while already there).
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @param {boolean} params.hasConfigurations
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFloorPlanViewedEvent({ planId, specId, lotId, hasConfigurations }) {
  return {
    eventName: 'native_map_floor_plan_viewed',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lotId,
      has_configurations: hasConfigurations,
    },
  };
}

/**
 * Fires when the user switches between the Floor Plan Detail view's own tabs (Lot & elevation /
 * Floor plan configuration).
 * @param {object} params
 * @param {string} params.tabSelected - internal tab constant (constants.js)
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFloorPlanDetailTabSelectedEvent({ tabSelected, planId, specId, lotId }) {
  return {
    eventName: 'native_map_details_tab_selected',
    customProperties: {
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      tab_selected: getFloorPlanDetailTabTrackingValue(tabSelected),
    },
  };
}

/**
 * Fires on the mobile-only "View floor plan" button click (Floor Plan Configuration tab).
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildViewFloorPlanClickedEvent({ planId, specId, lotId }) {
  return {
    eventName: 'native_map_view_floor_plan_clicked',
    customProperties: {
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
    },
  };
}

/**
 * Fires when the Floor Plan Configuration tab's "No configurations available" empty state is
 * shown.
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildEmptyConfigViewedEvent({ planId, specId, lotId }) {
  return {
    eventName: 'native_map_empty_config_viewed',
    customProperties: {
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      reason: 'no_configurations_available',
    },
  };
}

/**
 * "Similar homes available" accordion (under a Sold Home Card) expanded or collapsed.
 * Fires on both desktop and mobile — see plugins/tracking.plugin.js#onSimilarHomesToggled. Segment
 * gets a distinct eventName per direction, unlike GA4's shared native_map_events + click_target.
 * @param {object} params
 * @param {boolean} params.expanded
 * @param {number} params.lotId
 * @param {number} params.similarHomesCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSimilarHomesToggledEvent({ expanded, lotId, similarHomesCount }) {
  return {
    eventName: expanded ? 'native_map_similar_homesexp' : 'native_map_similar_homescolp',
    customProperties: { lot_id: lotId, similar_homes_count: similarHomesCount },
  };
}

/**
 * A similar-home card (under a Sold Home Card's "Similar homes available" list) was clicked.
 * Fires on both desktop and mobile. `planId`/`specId` are mutually exclusive — see
 * redux/observables.js#similarHomeCardClickedOccurred$.
 * @param {object} params
 * @param {number} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.position - 1-based card position within the similar-homes list
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSimilarHomeCardClickedEvent({ lotId, planId, specId, position }) {
  return {
    eventName: 'native_map_smlr_hm_clicked',
    customProperties: { lot_id: lotId, plan_id: planId, spec_id: specId, list_position: position },
  };
}

/**
 * Mobile-only Sold list "See All" button clicked. No properties.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSoldSeeAllClickedEvent() {
  return { eventName: 'native_map_soldlots_seeall', customProperties: {} };
}
